// filepath: apps/web/src/features/cabinet/RequiredUserParameters/RequiredUserParametersPage.states.tsx
//
// FR-UI-CABINET-3 — per-state components for the 5 mandatory renderer
// states (FR-UI-CORE-10): loading / fetch-failure / permission-denied /
// stale-version / rate-limit. Plus the empty state (when the tenant
// has no required parameters — the empty-state design at
// docs/design/FR-UI-CABINET-3/composition/required-user-parameters/empty.html).
//
// All chrome strings are sourced from the i18n bundle (FR-UI-CORE-8);
// no hardcoded English in this file. No `dangerouslySetInnerHTML`
// anywhere — every server-supplied string renders as a text node
// (INV-9).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Button } from "@/design-system/Button";
import type { RequiredParameter } from "./types";

/** Loading skeleton — matches the FR-UI-CORE-10 loading contract. */
export function RequiredParametersLoading(): React.ReactElement {
  return (
    <div
      data-testid="required-user-parameters--loading"
      aria-busy="true"
      style={{
        padding: "32px",
        color: "#6b7280",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div
        style={{
          height: "28px",
          width: "60%",
          background: "#e5e7eb",
          borderRadius: "var(--radius-md)",
          marginBottom: "16px",
        }}
      />
      <div
        style={{
          height: "12px",
          width: "40%",
          background: "#e5e7eb",
          borderRadius: "var(--radius-md)",
          marginBottom: "24px",
        }}
      />
      <div
        style={{
          height: "120px",
          background: "#f3f4f6",
          borderRadius: "var(--radius-md)",
        }}
      />
    </div>
  );
}

/** Empty state — tenant has no user_profile_form (or no required fields). */
export function RequiredParametersEmpty(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="required-user-parameters--empty"
      role="status"
      style={{
        padding: "32px",
        fontFamily: "system-ui, sans-serif",
        color: "#4b5563",
      }}
    >
      <h2 style={{ fontSize: "20px", marginBottom: "8px", color: "#111827" }}>
        {t(
          "cabinet.required_user_parameters.empty.title",
          "No required parameters",
        )}
      </h2>
      <p style={{ fontSize: "14px" }}>
        {t(
          "cabinet.required_user_parameters.empty.body",
          "Your tenant has not configured any required user parameters.",
        )}
      </p>
    </div>
  );
}

/** Fetch failure — generic retry surface (mirrors FetchError). */
export function RequiredParametersFetchFailure({
  onRetry,
}: {
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="required-user-parameters--fetch-failure"
      role="alert"
      style={{
        padding: "32px",
        fontFamily: "system-ui, sans-serif",
        color: "#4b5563",
      }}
    >
      <h2 style={{ fontSize: "20px", marginBottom: "8px", color: "#111827" }}>
        {t(
          "cabinet.required_user_parameters.fetch_failure.title",
          "We could not load this page",
        )}
      </h2>
      <p style={{ fontSize: "14px", marginBottom: "16px" }}>
        {t(
          "cabinet.required_user_parameters.fetch_failure.body",
          "Please try again.",
        )}
      </p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {t("cabinet.required_user_parameters.retry", "Retry")}
      </Button>
    </div>
  );
}

/** Permission denied — 403 (defense-in-depth: cross-user attempts). */
export function RequiredParametersPermissionDenied(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="required-user-parameters--permission-denied"
      role="alert"
      style={{
        padding: "32px",
        fontFamily: "system-ui, sans-serif",
        color: "#4b5563",
      }}
    >
      <h2 style={{ fontSize: "20px", marginBottom: "8px", color: "#111827" }}>
        {t(
          "cabinet.required_user_parameters.permission_denied.title",
          "You don't have access to this",
        )}
      </h2>
      <p style={{ fontSize: "14px" }}>
        {t(
          "cabinet.required_user_parameters.permission_denied.body",
          "Contact your administrator if you believe this is a mistake.",
        )}
      </p>
    </div>
  );
}

/** Stale version — 409 (FR-UI-CORE-6 conflict UX, surface-level). */
export function RequiredParametersStaleVersion({
  currentRowVersion,
  onRefetch,
}: {
  currentRowVersion: number;
  onRefetch: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="required-user-parameters--stale-version"
      role="alert"
      style={{
        padding: "32px",
        fontFamily: "system-ui, sans-serif",
        color: "#4b5563",
      }}
    >
      <h2 style={{ fontSize: "20px", marginBottom: "8px", color: "#111827" }}>
        {t(
          "cabinet.required_user_parameters.stale_version.title",
          "Version conflict",
        )}
      </h2>
      <p style={{ fontSize: "14px", marginBottom: "16px" }}>
        {t(
          "cabinet.required_user_parameters.stale_version.body",
          "This page was modified by another session. Current row version: {{version}}.",
          { version: String(currentRowVersion) },
        )}
      </p>
      <Button variant="primary" size="sm" onClick={onRefetch}>
        {t(
          "cabinet.required_user_parameters.stale_version.refetch",
          "Get latest version",
        )}
      </Button>
    </div>
  );
}

/** Rate-limited backpressure (FR-NFR-3 / FR-GRO-4 429). */
export function RequiredParametersRateLimit(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid="required-user-parameters--rate-limit"
      role="alert"
      style={{
        padding: "32px",
        fontFamily: "system-ui, sans-serif",
        color: "#4b5563",
      }}
    >
      <h2 style={{ fontSize: "20px", marginBottom: "8px", color: "#111827" }}>
        {t(
          "cabinet.required_user_parameters.rate_limit.title",
          "Too many requests",
        )}
      </h2>
      <p style={{ fontSize: "14px" }}>
        {t(
          "cabinet.required_user_parameters.rate_limit.body",
          "Please wait a moment and try again.",
        )}
      </p>
    </div>
  );
}

/** Read-only row for a single supplied (non-missing) field. */
export function RequiredParametersSuppliedRow({
  field,
}: {
  field: RequiredParameter;
}): React.ReactElement {
  const { t } = useTranslation();
  const label = readFieldLabel(field);
  return (
    <li
      data-testid={`required-user-parameters-row--${field.key}`}
      style={{
        padding: "12px 0",
        borderBottom: "1px solid #e5e7eb",
        listStyle: "none",
      }}
    >
      <div style={{ fontSize: "13px", color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: "15px", color: "#111827", marginTop: "4px" }}>
        {readFieldValue(field)}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: "#047857",
          marginTop: "4px",
        }}
      >
        {t("cabinet.required_user_parameters.supplied", "Provided")}
      </div>
    </li>
  );
}

/** Read-only row for a missing (required) field. */
export function RequiredParametersMissingRow({
  field,
}: {
  field: RequiredParameter;
}): React.ReactElement {
  const { t } = useTranslation();
  const label = readFieldLabel(field);
  return (
    <li
      data-testid={`required-user-parameters-row--${field.key}`}
      style={{
        padding: "12px 0",
        borderBottom: "1px solid #e5e7eb",
        listStyle: "none",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          color: "#6b7280",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>{label}</span>
        <span
          data-testid={`required-user-parameters-row--${field.key}--missing`}
          aria-label={t(
            "cabinet.required_user_parameters.missing_badge_aria",
            "Missing required parameter",
          )}
          style={{
            display: "inline-flex",
            padding: "2px 8px",
            fontSize: "11px",
            fontWeight: 600,
            color: "#b91c1c",
            background: "#fee2e2",
            borderRadius: "var(--radius-md)",
          }}
        >
          {t("cabinet.required_user_parameters.missing", "Missing")}
        </span>
      </div>
      <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>
        {t("cabinet.required_user_parameters.not_provided", "Not yet provided")}
      </div>
    </li>
  );
}

// readFieldLabel extracts a presentable label from the field's
// locale-map. Falls back to the field key when no label is set.
function readFieldLabel(field: RequiredParameter): string {
  const label = field.label ?? {};
  const locales = ["ru", "kk", "en", "uk", "be"] as const;
  for (const locale of locales) {
    const v = label[locale];
    if (typeof v === "string" && v.length > 0) return v;
  }
  // Fallback: any non-empty entry.
  for (const value of Object.values(label)) {
    if (typeof value === "string" && value.length > 0) return value;
  }
  return field.key;
}

// readFieldValue stringifies the supplied value. Values come back as
// raw JSON from the server (strings, numbers, booleans, arrays,
// objects); the SPA renders them verbatim as text nodes — never as
// HTML (INV-9).
function readFieldValue(field: RequiredParameter): string {
  const v = field.value;
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return "—";
  }
}
